"use client";

import React from "react";
import { PhoneCall, ShieldAlert, FileCheck2, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const technicalSpecs = [{
    label: "Regulatory Standard",
    value: "EPA & OSHA Class I Compliant"
  }, {
    label: "Containment Type",
    value: "HEPA Negative Pressure Enclosure"
  }, {
    label: "Regional Dispatch",
    value: "MD, DC & Northern Virginia"
  }];
  return <section id="hero" data-nav="dark" className="relative min-h-[640px] lg:min-h-[760px] w-full flex items-center justify-center overflow-hidden bg-foreground text-background">
      {/* Background Image with Responsive Attributes and Dark Industrial Scrim */}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/abatement_specialist_testing.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/abatement_specialist_testing.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/abatement_specialist_testing.png-wvc-sizes" alt="Certified abatement specialist wearing protective respirator sampling ceiling material" className="h-full w-full object-cover object-center" loading="eager" decoding="async" />
        {/* Multi-stop industrial dark scrim for contrast and legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/90 to-foreground/95" />
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <div className="max-w-3xl space-y-6">
          {/* Operational Status Spec Bar */}
          <div className="inline-flex items-center gap-2.5 rounded-md border border-border/20 bg-background/10 px-3.5 py-1.5 backdrop-blur-xs">
            <span className="relative flex size-2 shrink-0">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-accent" />
            </span>
            <span className="font-mono text-xs font-semibold tracking-wider text-background uppercase">
              24/7 Local Certified Response • MD / DC / NOVA
            </span>
          </div>

          {/* Heading */}
          <h1 className="font-default text-3xl font-extrabold tracking-tight text-background sm:text-4xl md:text-5xl lg:text-6xl">
            Asbestos &amp; Lead Abatement
          </h1>

          {/* Supporting Line */}
          <p className="font-default text-lg font-medium text-background/90 md:text-xl">
            Safe testing, negative-pressure containment, and certified hazardous removal across historic and mid-century structures in the Mid-Atlantic.
          </p>

          {/* Full Body Paragraph */}
          <p className="font-default text-base leading-relaxed text-background/80 md:text-lg">
            TriServe deploys direct local abatement technicians trained under strict federal, state, and local environmental standards. From non-invasive sampling and third-party laboratory verification to full-containment air clearance and chain-of-custody disposal, our crews control the hazard without subcontracts or delays.
          </p>

          {/* Action CTAs */}
          <div className="flex flex-col gap-3.5 pt-2 sm:flex-row sm:items-center">
            <Button asChild size="lg" className="h-12 bg-primary px-6 font-semibold text-primary-foreground shadow-md transition-transform hover:bg-primary/90 active:scale-95">
              <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4.5" />
                <span>Call Now</span>
                <span className="font-mono text-xs font-normal opacity-90">(855-435-6631)</span>
              </a>
            </Button>

            <Button asChild size="lg" className="h-12 border border-accent/40 bg-accent px-6 font-bold text-accent-foreground shadow-md transition-transform hover:bg-accent/90 active:scale-95">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          {/* Technical Spec Bar Motif */}
          <div className="pt-6">
            <div className="grid grid-cols-1 gap-3 border-t border-border/20 pt-6 sm:grid-cols-3">
              {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 text-background/60">
                    {i === 0 && <ShieldAlert className="size-3.5" />}
                    {i === 1 && <FileCheck2 className="size-3.5" />}
                    {i === 2 && <span className="size-1.5 rounded-full bg-primary" />}
                    <span className="font-mono text-[11px] uppercase tracking-wider">
                      {spec.label}
                    </span>
                  </div>
                  <span className="font-default text-xs font-medium text-background">
                    {spec.value}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}